// MARK://dhk8/admin-shell/pages/modules/MenusPage
// Composición de menús → dhk8-catalog-menus (en prod). Portado de dhk8-admin
// `/recetario/menus` — pero mejorado: en lugar del CRUD genérico de dhk8-admin,
// esta versión es un compositor real (menú → secciones → productos), con
// publicación y ventana horaria. Es lo que el comensal ve al escanear el QR.
//
// Barrido "todas a ficha de detalle" (Gustavo 2026-06-23): la lista navega a la
// ficha del menú (/menus/:id), donde vive el compositor (secciones/productos) y el
// botón Editar. "Nuevo menú" se queda como sheet aquí; publicar/eliminar quedan
// como acciones rápidas por fila.
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Trash2, CheckCircle2, BookOpen, ChevronRight } from 'lucide-react'
import * as menus from '../../lib/clients/menus.js'
import { requireTenant } from '../../lib/api.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

export default function MenusPage() {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { toast } = useToast()
  const list = useAsync((s) => menus.listMenus(s))

  const [busy, setBusy] = useState(false)

  // create-menu sheet
  const [creating, setCreating] = useState(false)
  const [menuForm, setMenuForm] = useState({ name: '', daysOfWeek: [] as number[], startTime: '', endTime: '' })
  const [menuError, setMenuError] = useState<string | null>(null)

  // Abrir el alta directo cuando se llega con ?new=1 (botón "Crear menú personalizado"
  // de la ficha del menú automático). Limpia el query param tras abrir.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('new') != null) {
      setMenuForm({ name: '', daysOfWeek: [], startTime: '', endTime: '' })
      setMenuError(null)
      setCreating(true)
      const next = new URLSearchParams(searchParams); next.delete('new'); setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function act(fn: () => Promise<unknown>, okMsg: string, errMsg: string) {
    setBusy(true)
    try { await fn(); toast(okMsg, 'success'); list.reload() }
    catch (e) { toast(e instanceof Error ? e.message : errMsg, 'danger') }
    finally { setBusy(false) }
  }

  async function saveMenu() {
    setMenuError(null)
    if (!menuForm.name.trim()) { setMenuError('El nombre es obligatorio.'); return }
    setBusy(true)
    try {
      const created = await menus.createMenu({
        name: menuForm.name.trim(),
        daysOfWeek: menuForm.daysOfWeek,
        ...(menuForm.startTime ? { startTime: menuForm.startTime } : {}),
        ...(menuForm.endTime ? { endTime: menuForm.endTime } : {}),
      })
      setCreating(false); setMenuForm({ name: '', daysOfWeek: [], startTime: '', endTime: '' })
      toast('Menú creado', 'success')
      // Lleva al usuario a la ficha del menú recién creado para armar sus secciones.
      navigate(`/menus/${created.id}`)
    } catch (e) {
      setMenuError(e instanceof Error ? e.message : 'No se pudo crear el menú.')
    } finally { setBusy(false) }
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Menús</h1><p className="adm-page__sub">Composición del menú: secciones, productos y ventana horaria</p></div>
        <div className="adm-page__actions">
          <button className="adm-btn adm-btn--primary" onClick={() => { setMenuForm({ name: '', daysOfWeek: [], startTime: '', endTime: '' }); setMenuError(null); setCreating(true) }}><Plus size={16} /> Nuevo menú</button>
        </div>
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {list.data.length === 0 && <div className="adm-card adm-card--pad"><div className="adm-empty">Aún no hay menús. El comensal verá el menú que publiques aquí al escanear el QR.</div></div>}
          {list.data.length > 0 && (
            <div className="adm-card adm-card--pad0">
              <div className="adm-list">
                {list.data.map((m) => {
                  const productCount = m.sections.reduce((n, s) => n + s.products.length, 0)
                  const isAuto = m.id === requireTenant()
                  return (
                    <div key={m.id} className="adm-row" style={{ cursor: 'pointer' }} onClick={() => navigate(`/menus/${m.id}`)}>
                      <div className="adm-row__main">
                        <div className="adm-row__title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <BookOpen size={14} aria-hidden style={{ opacity: 0.6, flexShrink: 0 }} />
                          {m.name}
                        </div>
                        <div className="adm-row__meta">
                          {menus.scheduleLabel(m)} · {m.sections.length} {m.sections.length === 1 ? 'sección' : 'secciones'} · {productCount} producto{productCount === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div className="adm-row__tail" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {isAuto ? (
                          <span className="adm-badge" title="Menú automático armado de tu catálogo. Crea un menú con &quot;Nuevo menú&quot; para personalizar horario y secciones.">Automático · catálogo</span>
                        ) : (
                          <>
                            <StatusPill tone={lifecycleTone(m.active)} label={m.active ? 'Activo' : 'Inactivo'} />
                            {m.publishedAt
                              ? <StatusPill tone="ok" label="Publicado" />
                              : <StatusPill tone="info" label="Borrador" />}
                            {!m.publishedAt && <button className="adm-btn adm-btn--ghost adm-btn--sm" disabled={busy} onClick={(e) => { e.stopPropagation(); void act(() => menus.publishMenu(m.id), 'Menú publicado', 'No se pudo publicar.') }} aria-label="Publicar" title="Publicar"><CheckCircle2 size={15} /></button>}
                            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={busy} onClick={async (e) => { e.stopPropagation(); const ok = await confirm({ title: `¿Eliminar el menú "${m.name}"?`, message: 'Se eliminarán sus secciones y productos. Esta acción no se puede deshacer.', tone: 'danger', confirmLabel: 'Eliminar' }); if (ok) void act(() => menus.deleteMenu(m.id), 'Menú eliminado', 'No se pudo eliminar.') }} aria-label="Eliminar"><Trash2 size={15} /></button>
                          </>
                        )}
                        <ChevronRight size={16} aria-hidden style={{ opacity: 0.5, flexShrink: 0 }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create menu */}
      <Sheet
        open={creating}
        title="Nuevo menú"
        onClose={() => { setCreating(false); setMenuError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreating(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveMenu} disabled={busy || !menuForm.name.trim()}>{busy ? 'Guardando…' : 'Crear menú'}</button>
        </>}
      >
        {menuError && <div className="adm-banner adm-banner--danger" role="alert">{menuError}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Nombre</span>
          <input className="adm-input" value={menuForm.name} onChange={(e) => setMenuForm({ ...menuForm, name: e.target.value })} placeholder="Menú principal" />
        </label>
        <div className="adm-field">
          <span className="adm-field__label">Días de la semana (vacío = todos)</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {menus.DAY_LABELS.map((d, i) => {
              const on = menuForm.daysOfWeek.includes(i)
              return (
                <button
                  key={i}
                  type="button"
                  className={`adm-btn adm-btn--sm${on ? ' adm-btn--primary' : ' adm-btn--ghost'}`}
                  onClick={() => setMenuForm({ ...menuForm, daysOfWeek: on ? menuForm.daysOfWeek.filter((x) => x !== i) : [...menuForm.daysOfWeek, i] })}
                >{d}</button>
              )
            })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <label className="adm-field" style={{ flex: 1 }}>
            <span className="adm-field__label">Desde</span>
            <input className="adm-input" type="time" value={menuForm.startTime} onChange={(e) => setMenuForm({ ...menuForm, startTime: e.target.value })} />
          </label>
          <label className="adm-field" style={{ flex: 1 }}>
            <span className="adm-field__label">Hasta</span>
            <input className="adm-input" type="time" value={menuForm.endTime} onChange={(e) => setMenuForm({ ...menuForm, endTime: e.target.value })} />
          </label>
        </div>
      </Sheet>
    </div>
  )
}
